'use client';

import { useContext, useEffect, useMemo, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import AuthContext from '@/providers/auth-context';
import { sendErrorLog } from '@/utils/http';

declare const process: {
    env: {
        NEXT_PUBLIC_LONGTASK_MONITOR?: string;
    };
};

const DEFAULT_SAMPLE_RATE = 1; // 1 = 100%
const DEFAULT_MIN_DURATION_MS = 200; // only log long tasks >= 200ms
const DEFAULT_RATE_LIMIT_MS = 60_000; // 1 log / minute
const QUEUE_KEY = 'longTaskQueue';
const QUEUE_LIMIT = 50;
const RETRY_INTERVAL_MS = 30_000;

function shouldEnableMonitoring() {
    if (typeof window === 'undefined') return false;

    const envFlag = process.env.NEXT_PUBLIC_LONGTASK_MONITOR;
    if (envFlag && envFlag !== '0' && envFlag !== 'false') return true;

    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('perf') === '1';
}

function isSampled(sampleRate) {
    if (sampleRate >= 1) return true;
    if (sampleRate <= 0) return false;
    return Math.random() < sampleRate;
}

function enqueuePayload(payload) {
    if (typeof window === 'undefined') return;
    try {
        const existing = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
        existing.push({ payload, ts: Date.now(), retry: 0 });
        while (existing.length > QUEUE_LIMIT) existing.shift();
        localStorage.setItem(QUEUE_KEY, JSON.stringify(existing));
    } catch (_) {
        // best-effort only
    }
}

function flushQueue() {
    if (typeof window === 'undefined') return;
    let queue;
    try {
        queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    } catch (_) {
        queue = [];
    }

    if (!Array.isArray(queue) || queue.length === 0) return;

    const remaining = [];
    let pending = queue.length;

    const finalize = () => {
        if (pending > 0) return;
        try {
            localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
        } catch (_) {
            // best-effort only
        }
    };

    queue.forEach((item) => {
        sendErrorLog({ params: item.payload })
            .then(() => {
                pending -= 1;
                finalize();
            })
            .catch(() => {
                remaining.push({ ...item, retry: (item.retry || 0) + 1 });
                pending -= 1;
                finalize();
            });
    });
}

export default function LongTaskMonitor() {
    const { userData } = useContext(AuthContext);
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const lastSentRef = useRef(0);
    const enabled = useMemo(() => shouldEnableMonitoring(), []);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return;
        if (!isSampled(DEFAULT_SAMPLE_RATE)) return;

        let observer;
        try {
            observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const now = Date.now();

                for (const entry of entries) {
                    const duration = Math.round(entry.duration);
                    if (duration < DEFAULT_MIN_DURATION_MS) continue;
                    if (now - lastSentRef.current < DEFAULT_RATE_LIMIT_MS) continue;

                    lastSentRef.current = now;

                    const payload = {
                        message: `Long Task detected (${duration}ms)` ,
                        stack: `name=${entry.name || 'longtask'} | duration=${duration}ms | start=${Math.round(entry.startTime)}ms`,
                        url: `${window.location.pathname}${window.location.search}`,
                        useragent: navigator.userAgent,
                        source: 'long-task-monitor',
                        type: 'performance',
                        duration,
                        path: pathname,
                        query: searchParams?.toString() || '',
                        user_id: userData?.id || 0,
                        device: window.innerWidth <= 768 ? 'mobile' : 'desktop'
                    };

                    sendErrorLog({ params: payload }).catch(() => {
                        enqueuePayload(payload);
                    });

                    break; // rate-limited: one per batch
                }
            });

            observer.observe({ type: 'longtask', buffered: true });
        } catch (err) {
            // if longtask not supported, ignore
        }

        return () => {
            if (observer) observer.disconnect();
        };
    }, [enabled, pathname, searchParams, userData?.id]);

    useEffect(() => {
        if (!enabled) return;
        if (typeof window === 'undefined') return;

        flushQueue();

        const handleOnline = () => flushQueue();
        const intervalId = window.setInterval(() => flushQueue(), RETRY_INTERVAL_MS);
        window.addEventListener('online', handleOnline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.clearInterval(intervalId);
        };
    }, [enabled]);

    return null;
}
